import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, History } from "lucide-react";
import TechnicianFormDialog from "@/components/admin/TechnicianFormDialog";
import CaseLogList from "@/components/admin/CaseLogList";

function HistoryDialog({ tech, onClose }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    if (!tech) { return; }
    api.get(`/admin/case-logs?technician_id=${tech.id}`).then((r) => setLogs(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [tech]);
  if (!tech) return null;
  return (
    <Dialog open={!!tech} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="tech-history-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{tech.name} · case log history</DialogTitle></DialogHeader>
        <CaseLogList logs={logs} />
      </DialogContent>
    </Dialog>
  );
}

export default function TechniciansPanel() {
  const [techs, setTechs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);

  const load = async () => {
    try { setTechs((await api.get("/admin/technicians")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="soft-card p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="font-display text-xl font-bold">Technicians</div>
        <Button data-testid="admin-add-tech-btn" onClick={() => setEditing("new")} className="rounded-full bg-brand hover:bg-brand-dark text-white font-semibold h-10 px-4 shadow-btn">
          <Plus className="w-4 h-4 mr-2" /> Add technician
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Technician</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Specialisation</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead>Avg resolution</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {techs.map((t) => (
              <TableRow key={t.id} data-testid={`admin-tech-row-${t.id}`}>
                <TableCell><div className="font-semibold">{t.name}</div><div className="text-xs text-slate-500">{t.email}</div></TableCell>
                <TableCell className="font-mono text-sm">{t.phone}</TableCell>
                <TableCell>{t.specialisation || <span className="text-slate-500">—</span>}</TableCell>
                <TableCell data-testid={`admin-tech-assigned-${t.id}`}>{t.sessions_assigned}</TableCell>
                <TableCell data-testid={`admin-tech-completed-${t.id}`}>{t.sessions_completed}</TableCell>
                <TableCell data-testid={`admin-tech-avg-${t.id}`}>{t.avg_resolution_minutes == null ? "—" : `${t.avg_resolution_minutes} min`}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" variant="outline" className="border border-slate-200 font-semibold mr-2" data-testid={`admin-tech-edit-${t.id}`} onClick={() => setEditing(t)}>
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border border-slate-200 font-semibold" data-testid={`admin-tech-history-${t.id}`} onClick={() => setHistory(t)}>
                    <History className="w-4 h-4 mr-1" /> Logs
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {techs.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No technicians yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TechnicianFormDialog tech={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <HistoryDialog tech={history} onClose={() => setHistory(null)} />
    </div>
  );
}
